// Frontend twin of the backend ControlPlaneAccessPolicy: the "platform-admin / control-plane only"
// access rules in one place. Path *values* come from the generated PAGES map; this file holds the
// policy (prefixes + predicates) that code consumes instead of re-typing "/cheats" everywhere.
import { PAGES } from './pagePaths.generated.js';

// Developer Tools (Cheats) page/route prefix.
export const CHEATS_PREFIX = '/cheats';

// True when a path is Developer Tools / Cheats (super-user-only, not grantable via RBAC).
export const isCheatsPath = (pathname) => !!pathname && pathname.startsWith(CHEATS_PREFIX);

// Paths a Super User (limited control-plane role) may reach; the route guard redirects them
// elsewhere. Game Settings is the games LIST only; account/notification chrome stays allowed so the
// persistent UI doesn't break.
export const SUPER_USER_ALLOWED_PREFIXES = [
    CHEATS_PREFIX,                       // Developer Tools (own shell)
    '/admin-settings/role-access-mgmt',  // Page/Component Management (Role Management is org-owner-only, blocked by RBAC)
    PAGES.AI_CREDITS,                   // read-only cross-org AI credits oversight
    '/profile', '/releases', '/help', '/notifications', '/account', '/not-authorized',
];

export const isSuperUserAllowedPath = (pathname) => {
    if (!pathname) return false;
    if (pathname === PAGES.GAMES) return true; // games LIST only (not edit/workspace/etc.)
    return SUPER_USER_ALLOWED_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));
};

/**
 * Where a Super User lands - after login, and when the route guard bounces them: Management's Games list, the
 * first Management page they may open. It used to be Developer Tools, whose sidebar fell back to the member hub
 * (Homepage, Components, Games, Portfolio) - pages a Super User is bounced from anyway.
 */
export const SUPER_USER_LANDING = PAGES.GAMES;

// Where everyone else lands, after sign-in and wherever the app sends them "home": routes/LandingRedirect.jsx.
export const LANDING_PATH = '/landing';

/**
 * A nav tree pruned to what a Super User may open (isSuperUserAllowedPath): a leaf stays only when its path is
 * allowed; a container stays when any child does. So the Management menu shows Games, Pages, Roles and AI
 * Credits - not Organizations or Users, which the route guard would bounce them from.
 */
export const superUserNav = (links = []) => (links || []).flatMap((link) => {
    if (!link) return [];
    const children = link.subItems ? superUserNav(link.subItems) : null;
    if (children && children.length) return [{ ...link, subItems: children }];
    if (children && !children.length && !link.path) return [];
    return link.path && isSuperUserAllowedPath(link.path) ? [link] : [];
});
